import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Download,
  Image as ImageIcon,
  MapPin,
  Building2,
} from "lucide-react";
import { DashboardLayout } from "../../components/layouts/DashboardLayout";
import Logo from "../../components/shared/Logo";
import {
  getSinglePropertyTransaction,
  getTransactionByReference,
  downloadReceiptPDF,
} from "../../services/krent.service";
import styles from "./BookingReceipt.module.css";

const money = (value: any) =>
  value == null || value === "" || !Number.isFinite(Number(value))
    ? "—"
    : `₦${Number(value).toLocaleString("en-NG")}`;
const date = (value: any, withTime = false) => {
  if (!value || Number.isNaN(new Date(value).getTime())) return "—";
  return new Date(value).toLocaleString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
    ...(withTime ? ({ hour: "2-digit", minute: "2-digit" } as const) : {}),
  });
};
const Row = ({ label, value }: { label: string; value: any }) => (
  <div>
    <dt>{label}</dt>
    <dd>{value == null || value === "" ? "—" : value}</dd>
  </div>
);

export default function BookingReceipt() {
  const { id } = useParams();
  const [transaction, setTransaction] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [downloadError, setDownloadError] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError("");
    const load = async () => {
      try {
        if (!id) throw new Error("Booking not found.");
        const res: any = /^[a-f0-9]{24}$/i.test(id)
          ? await getSinglePropertyTransaction(id, controller.signal)
          : await getTransactionByReference(id);
        if (!res || res.success === false)
          throw new Error(res?.message || "Could not load receipt details.");
        if (!controller.signal.aborted) {
          setTransaction(res.data || res);
          setImageFailed(false);
        }
      } catch (err: any) {
        if (!controller.signal.aborted)
          setError(err?.message || "Could not load receipt details.");
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };
    load();
    return () => controller.abort();
  }, [id, retry]);
  const listing = transaction?.listing || {};
  const bill = transaction?.bill?.[0] || {};
  const payment = (Array.isArray(transaction?.payment) ? transaction.payment[0] : transaction?.payment) || {};
  const isRent = listing.intent === "long lease";
  const back = isRent ? "/my-bookings?tab=rent" : "/my-bookings";
  const reference = payment.reference;
  const location = [listing.location?.city, listing.location?.state]
    .filter(Boolean)
    .join(", ");
  const kind =
    typeof listing.kind === "string" ? listing.kind : listing.kind?.name;
  const propertyType = [
    kind,
    listing.bedrooms != null ? `${listing.bedrooms} bed` : null,
    listing.bathrooms != null ? `${listing.bathrooms} bath` : null,
  ]
    .filter(Boolean)
    .join(" · ");
  const download = async () => {
    if (!reference || downloading) return;
    setDownloading(true);
    setDownloadError("");
    try {
      const blob = await downloadReceiptPDF(reference);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = "krent-receipt.pdf";
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err: any) {
      setDownloadError(
        err.message || "Unable to download receipt. Please try again.",
      );
    } finally {
      setDownloading(false);
    }
  };
  return (
    <DashboardLayout
      title="Booking receipt"
      subtitle="Here’s an overview of your payment details."
    >
      <main className={`${styles["page"]} font-onest`}>
        <Link className={styles["back"]} to={back}>
          <ArrowLeft size={17} />
          Back to bookings
        </Link>
        {loading ? (
          <p role="status">Loading receipt…</p>
        ) : error ? (
          <div role="alert">
            <p>{error}</p>
            <button type="button" onClick={() => setRetry((n) => n + 1)}>
              Try again
            </button>
          </div>
        ) : (
          <>
            <header className={styles["banner"]}>
              <img className={styles["watermark"]} src="/assets/krent-receipt.svg" alt="" aria-hidden="true" />
              <div className={styles["bannerContent"]}>
              <Logo width="112px" />
              <h1>{money(bill.total)}</h1>
              <p>
                {isRent ? "Rent" : "Booking"} receipt · {listing.title || "—"}
              </p>
              <small>
                Booking number ·{" "}
                <span>
                  {transaction.bookingReference ||
                    transaction.referenceId ||
                    "—"}
                </span>
              </small>
              </div>
            </header>
            <article className={styles["receipt"]}>
              <div className={styles["body"]}>
                <h2>Property details</h2>
                <div className={styles["property"]}>
                  {listing.coverImage && !imageFailed ? (
                    <img
                      src={listing.coverImage}
                      alt={listing.title || "Property"}
                      onError={() => setImageFailed(true)}
                    />
                  ) : (
                    <div className={styles["imagePlaceholder"]}>
                      <ImageIcon aria-label="No property image" />
                    </div>
                  )}
                  <div>
                    <h3>{listing.title || "—"}</h3>
                    <p>
                      <MapPin size={14} />
                      {location || "—"}
                      <span>
                        Ref: {listing.slug || listing.referenceId || "—"}
                      </span>
                    </p>
                  </div>
                  {!isRent && typeof listing.instantBooking === "boolean" && (
                    <span className={styles["badge"]}>
                      {listing.instantBooking
                        ? "Instant Property"
                        : "Non Instant Property"}
                    </span>
                  )}
                </div>
                <dl className={styles["rows"]}>
                  <Row label="Property type" value={propertyType} />
                  <Row label="Property ID" value={listing.id || listing._id} />
                </dl>
                <h2>Payment info</h2>
                <dl className={styles["rows"]}>
                  <Row
                    label={isRent ? "Rent date" : "Booking dates"}
                    value={`${date(transaction.arrival_date)} - ${date(transaction.departure_date)}`}
                  />
                  <Row
                    label="Payment method"
                    value={
                      payment.channel ||
                      payment.method ||
                      transaction.paymentMethod
                    }
                  />
                  <Row
                    label="Payment date"
                    value={date(payment.paidAt || payment.paid_at || payment.paymentDate || payment.payment_date || transaction.paidAt || transaction.paymentDate || payment.createdAt, true)}
                  />
                  <Row label="Transaction ref" value={reference} />
                </dl>
                <h2>Payment breakdown</h2>
                <dl className={styles["rows"]}>
                  <Row
                    label={isRent ? (bill.rentCycle === "monthly" ? "Monthly rent" : "Annual rent") : "Property price"}
                    value={money(bill.rentPerMonth ?? bill.rentPerYear ?? bill.propertyPrice)}
                  />
                  {!isRent && <Row label="Legal fee" value={money(bill.legalFee)} />}
                  <Row
                    label="Service charge"
                    value={money(bill.serviceCharge)}
                  />
                  <Row
                    label={isRent ? "Administrative fee" : "Agency fee"}
                    value={money(isRent ? bill.administrativeFee ?? (bill.agencyFee || 0) + (bill.legalFee || 0) : bill.agencyFee)}
                  />
                  <Row
                    label="Caution fee (refundable)"
                    value={money(bill.cautionFee)}
                  />
                </dl>
                <dl className={`${styles["rows"]} ${styles["total"]}`}>
                  <Row label="Total paid" value={money(bill.total)} />
                </dl>
              </div>
              <footer className={styles["footer"]}>
                <div>
                  Receipt ref: {payment.receiptReference || reference || "—"}
                  <br />
                  Issued by Krent
                </div>
                <div>
                  Questions?
                  <br />
                  <a href="mailto:support@krent.ng">support@krent.ng</a>
                </div>
              </footer>
            </article>
            {downloadError && (
              <p role="alert" className="text-danger mt-3">
                {downloadError}
              </p>
            )}
            {!reference && (
              <p className="mt-3">
                The PDF receipt is not available for this payment yet.
              </p>
            )}
            <div className={styles["actions"]}>
              <button
                type="button"
                onClick={download}
                disabled={downloading || !reference}
              >
                <Download size={18} className={styles["downloadIcon"]} color="#fff" />
                {downloading ? "Downloading…" : "Download PDF"}
              </button>
              <Link to={back}>
                <Building2 size={18} />
                Back to Bookings
              </Link>
            </div>
          </>
        )}
      </main>
    </DashboardLayout>
  );
}
